/* eslint-disable class-methods-use-this */
/* eslint-disable no-restricted-globals */
import { SupabaseClient } from '@supabase/supabase-js';
import { Database, Note, TypeOfNote, UUID, Profile, RelatedResult, isUUID, AnnoteIndexEntry } from '@annote/types';

import { selectNoteFields } from '@annote/core/utils/notes';
import { authLogger } from '@annote/core/utils/debug';
import { ServerDB, IDatabase } from './types';

// embedding
// fts

// hard coded in as env variables are different in core vs application
// TODO - this should be a config variable
const allowedUrls = [
  'https://annote.com/',
  'https://api.annote.com/',
  'https://ai.annote.com/',
  'https://vyffaascqnqmuqgagztb.supabase.co/',
  'https://vyffaascqnqmuqgagztb.functions.supabase.co/',
  'https://omfbwytqxdkkrbesquse.supabase.co/',
  'https://omfbwytqxdkkrbesquse.functions.supabase.co/',
  'http://localhost:54321/',
  'http://localhost:3013/',
  'http://localhost:3011/',
];

export class SupabaseDatabase implements ServerDB {
  db: SupabaseClient<Database>;

  serverDB?: SupabaseDatabase;

  private functionsUrl: string;

  userId?: UUID;

  profile: Profile | null = null;

  private isInitializing = false;

  // a fucntion to call when the user needs reauthenticating
  public readonly onReauth: () => Promise<boolean>;

  constructor(db: SupabaseClient<Database>, functionsUrl?: string, onReauth?: () => Promise<boolean>) {
    this.db = db;
    if (!functionsUrl) throw new Error('SupabaseDatabase constructor - No functions URL found');
    this.functionsUrl = functionsUrl;
    this.onReauth = onReauth || (async () => false);
    this.serverDB = this;
  }

  // cache the userId
  async init(): Promise<void> {
    authLogger.debug('[supabaseDb.init] Starting initialization');

    // makes sure we don't double initialize - strict mode can try to double run this
    if (this.isInitializing) {
      authLogger.debug('[supabaseDb.init] Already initializing, skipping');
      return;
    }
    this.isInitializing = true;

    try {
      // Get userId from session
      authLogger.debug('[supabaseDb.init] Calling getSessionUserId()...');
      const userId = await this.getSessionUserId();
      authLogger.debug('[supabaseDb.init] getSessionUserId() returned:', userId ? 'has userId' : 'no userId');

      // If we have a userId, fetch the profile
      if (userId) {
        this.userId = userId;
        authLogger.debug('[supabaseDb.init] Fetching profile...');
        this.profile = await this.getProfile();
        authLogger.debug('[supabaseDb.init] Profile fetched:', this.profile ? 'has profile' : 'no profile');
      } else {
        authLogger.debug('[supabaseDb.init] No userId, skipping profile fetch');
      }
    } finally {
      this.isInitializing = false;
      authLogger.debug('[supabaseDb.init] Initialization complete');
    }
  }

  // notes query shortcut - returns on the right fields
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _query = (): any => {
    return this.db.from('Notes').select(selectNoteFields);
  };

  private isUrlBlocked = (url: string): boolean => {
    const { host } = new URL(url);
    if (!this.profile) throw new Error('isUrlBlocked - called but no Profile found');
    return this.profile.blockedHosts.includes(host);
  };

  // if no ids provided all notes are returned
  getNotes = async (ids?: UUID[]): Promise<Note[]> => {
    if (!this.userId) throw new Error('User not authenticated - getNotes');

    let query = this._query().eq('userId', this.userId).is('deletedAt', null);
    const cleanIds = ids?.filter((id) => isUUID(id));
    if (cleanIds && cleanIds.length > 0) query = query.in('id', cleanIds);

    const { data: notes, error } = await query;
    if (error) throw error;
    return notes as Note[];
  };

  getNotesSince = async (since: string): Promise<Note[]> => {
    if (!this.userId) throw new Error('User not authenticated - getNotesSince');

    const { data: notes, error } = await this._query().gt('syncedAt', since).eq('userId', this.userId);
    if (error) throw error;
    return notes as Note[];
  };

  getNotesByUrl = async (url: string): Promise<{ notes: Note[]; isBlocked: boolean }> => {
    // If user is not authenticated, return empty notes (don't throw)
    // This allows annote-embed to initialize before login without errors
    if (!this.userId) {
      return { notes: [], isBlocked: false };
    }

    if (this.isUrlBlocked && this.isUrlBlocked(url)) {
      return { notes: [], isBlocked: true };
    }
    const { data: notes, error } = await this._query().eq('url', url).eq('userId', this.userId).is('deletedAt', null);
    if (error) throw error;
    return { notes: notes as Note[], isBlocked: false };
  };

  getNoteById = async (id: UUID): Promise<Note | null> => {
    if (!this.userId) throw new Error('User not authenticated - getNoteById');

    const { data: notes, error } = await this._query()
      .eq('id', id)
      .eq('userId', this.userId) // extra check - even though row level security should be sufficient
      .is('deletedAt', null);
    if (error) throw error;
    if (notes.length > 0) {
      return notes[0] as Note;
    }
    return null;
  };

  getNoteByTitle = async (title: string): Promise<Note | null> => {
    if (!this.userId) throw new Error('User not authenticated - getNoteByTitle');

    const { data: notes, error } = await this._query()
      .eq('title', title)
      .eq('userId', this.userId) // extra check - even though row level security should be sufficient
      .is('deletedAt', null);
    if (error) throw error;
    if (notes.length > 0) {
      return notes[0] as Note;
    }
    return null;
  };

  private ensureValidSession = async (): Promise<void> => {
    // First check if we have a cached userId
    if (!this.userId) {
      throw new Error('User not authenticated - no userId');
    }

    // Then verify the session is actually valid
    try {
      const {
        data: { session },
        error,
      } = await this.db.auth.getSession();

      if (error) {
        console.error('Session validation error:', error);
        throw new Error(`Session validation failed: ${error.message}`);
      }

      if (!session) {
        this.userId = undefined;
        throw new Error('No valid session found - please log in again');
      }

      // Update userId if it changed
      if (session.user.id !== this.userId) {
        console.warn('UserId mismatch - updating from session');
        this.userId = session.user.id;
      }
    } catch (err) {
      console.error('Error validating session:', err);
      throw err;
    }
  };

  _latestNotes = async (types: TypeOfNote[] = ['takeaway', 'source'], limit: number = 100): Promise<Note[]> => {
    await this.ensureValidSession();

    let queryBuilder = this._query()
      .eq('userId', this.userId)
      .is('deletedAt', null)
      .order('syncedAt', { ascending: false })
      .limit(limit);
    if (types && types.length > 0) {
      queryBuilder = queryBuilder.in('type', types);
    }
    const { data: notes, error } = await queryBuilder;
    if (error) throw error;
    return notes as Note[];
  };

  searchNotes = async (query: string, types?: TypeOfNote[], limit: number = 10): Promise<Note[]> => {
    await this.ensureValidSession();

    if (query.length === 0) return this._latestNotes(types, limit);

    const searchQuery = query.trim().replace(/\s+/g, ' & ');
    // Use the hybrid_search function which handles userId and types filtering server-side
    const { data: notes, error } = await this.db.rpc('search_notes', {
      search_query: searchQuery,
      max_results: limit,
      types: types || null,
    });

    if (error) throw error;
    return (notes || []) as Note[];
  };

  // WRITE METHODS

  updateNotes = async (notes: Note[]): Promise<void> => {
    if (!this.userId) throw new Error('User not authenticated - updateNotes');
    // ensure authed userId and remove duplicate links/backlinks
    const removeDuplicates = (array: UUID[]): UUID[] => {
      return [...new Set(array)];
    };
    // Note: suggestionId is a UI-only property, not a database column.
    // We must strip it out before sending to the database.
    const authEnsuredNotes = notes.map((change) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { suggestionId, ...noteWithoutSuggestion } = change;
      return {
        ...noteWithoutSuggestion,
        userId: this.userId,
        // Ensure links and backlinks aren't duplicated (matches DexieDatabase behavior)
        links: removeDuplicates(change.links || []),
        backlinks: removeDuplicates(change.backlinks || []),
      };
    });
    const { error } = await this.db.from('Notes').upsert(authEnsuredNotes);
    if (error) throw error;
  };

  deleteNotes = async (notes: Note[]): Promise<void> => {
    if (!this.userId) throw new Error('User not authenticated - deleteNotes');
    // Logical delete Note by setting the deletedAt field
    const now = new Date().toISOString();
    const notesToDelete = notes.map((note) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { suggestionId, ...noteWithoutSuggestion } = note;
      return {
        ...noteWithoutSuggestion,
        userId: this.userId,
        deletedAt: note.deletedAt || now,
      };
    });

    const deletedIds = notesToDelete.map((dn) => dn.id);

    // get all the ids that are linked to the notes to delete
    // IMPORTANT: exclude the notes we're deleting to avoid duplicate upserts
    const allLinkedIds = Array.from(new Set(notesToDelete.flatMap((dn) => [...dn.links, ...dn.backlinks])));
    const idsToUpdate = allLinkedIds.filter((id) => !deletedIds.includes(id));

    console.log('supabaseDb deleting notes', notesToDelete.length, notesToDelete, 'allIdsToUpdate', idsToUpdate);
    // remove the deleted note from other notes links, backlinks, and createdFromIds
    const notesToUpdate = await this.getNotes(idsToUpdate);
    const connectedNotesToUpdate = notesToUpdate.map((n) => {
      const links = n.links?.filter((id) => !deletedIds.includes(id)) || [];
      const backlinks = n.backlinks?.filter((id) => !deletedIds.includes(id)) || [];
      // Clear createdFromId if it points to a deleted note (matches DexieDatabase behavior)
      const createdFromId = n.createdFromId && !deletedIds.includes(n.createdFromId) ? n.createdFromId : undefined;
      return { ...n, links, backlinks, createdFromId };
    });
    const allUpdatedNotes = [...connectedNotesToUpdate, ...notesToDelete];
    // Remove all links to the deleted note before marking it deleted
    console.log('supabaseDb updating notes', allUpdatedNotes.length, allUpdatedNotes);
    if (allUpdatedNotes.length > 0) {
      await this.updateNotes(allUpdatedNotes);
    }
    // double check that the notes are deleted
    // const notesThatShouldDeletedButArent = await this.db
    //   .from('Notes')
    //   .select('id')
    //   .eq('userId', this.userId)
    //   .is('deletedAt', null);
    // console.log(
    //   'supabaseDb notesThatShouldDeleted',
    //   notesThatShouldDeletedButArent.data?.length,
    //   'deleted ats are',
    //   notesThatShouldDeletedButArent.data?.map((n) => {
    //     return { id: n.id };
    //   }),
    // );
  };

  getProfile = async (forceRefresh: boolean = false): Promise<Profile | null> => {
    if (!this.userId) return null; // not initialized/authed yet

    // Return cached profile if available and not forcing refresh
    if (this.profile && !forceRefresh) {
      return this.profile;
    }

    // Fetch from database
    const { data: profile, error } = await this.db.from('Profiles').select().eq('userId', this.userId).single();
    if (error) throw error;

    // keep the cached this.profile updated
    this.profile = profile as Profile;
    return profile as Profile;
  };

  updateProfile = async (profile: Profile): Promise<void> => {
    if (!this.userId) throw new Error('User not authenticated - updateProfile');
    const { error } = await this.db.from('Profiles').upsert({ ...profile, userId: this.userId });
    if (error) throw error;
    // keep the cached this.profile updated
    this.profile = profile as Profile;
  };

  // Get the userId from the supabase session
  private getSessionUserId = async (): Promise<UUID | null> => {
    authLogger.debug('[supabaseDb.getSessionUserId] Starting, navigator.onLine:', navigator.onLine);

    if (!navigator.onLine) {
      authLogger.debug('[supabaseDb.getSessionUserId] Offline, returning null');
      return null;
    }

    try {
      authLogger.debug('[supabaseDb.getSessionUserId] Calling db.auth.getSession()...');

      // Add timeout to prevent hanging in Safari (known issue with Supabase + Safari)
      const timeoutMs = 5000;
      const getSessionPromise = this.db.auth.getSession();
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error(`getSession() timed out after ${timeoutMs}ms`)), timeoutMs);
      });

      const { data, error } = await Promise.race([getSessionPromise, timeoutPromise]);
      const session = data?.session;

      authLogger.debug('[supabaseDb.getSessionUserId] getSession() returned:', {
        hasSession: !!session,
        hasError: !!error,
        errorMessage: error?.message,
      });
      authLogger.debug('[supabaseDb.getSessionUserId] getSession completed within timeout');

      if (error) throw error;

      // Check if session exists and is not expired
      if (!session) {
        authLogger.debug('[supabaseDb.getSessionUserId] No session, returning null');
        return null;
      }

      // Validate token expiration
      if (session.expires_at) {
        const expiresAt = session.expires_at * 1000; // Convert to milliseconds
        const now = Date.now();
        authLogger.debug('[supabaseDb.getSessionUserId] Token expiration check:', {
          expiresAt: new Date(expiresAt).toISOString(),
          now: new Date(now).toISOString(),
          expired: now >= expiresAt,
        });
        if (now >= expiresAt) {
          authLogger.warn('[supabaseDb.getSessionUserId] Session token expired, clearing session');
          return null;
        }
      }

      const userId = session?.user?.id || null;
      authLogger.debug('[supabaseDb.getSessionUserId] Returning userId:', userId ? 'present' : 'null');
      return userId;
    } catch (err) {
      const isTimeout = err instanceof Error && err.message.includes('timed out');
      if (isTimeout) {
        authLogger.warn(
          '[supabaseDb.getSessionUserId] Timeout - getSession() hung (common in Safari).  The JWT session expired',
        );
        // Clear stale auth tokens to prevent repeated hangs on page refresh
        try {
          const storageKey = 'annote-auth';
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.removeItem(storageKey);
            authLogger.debug('[supabaseDb.getSessionUserId] Cleared stale auth token from localStorage');
          }
        } catch {
          // Ignore storage errors
        }
      } else {
        authLogger.error('[supabaseDb.getSessionUserId] Error:', err);
      }
      // Return null instead of throwing to allow the app to continue
      return null;
    }
  };

  isAuthenticated = async (): Promise<boolean> => {
    // Check if we have a cached userId first
    if (this.userId) {
      return true;
    }

    // Otherwise, check the session
    try {
      const userId = await this.getSessionUserId();
      if (userId) {
        this.userId = userId;
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  findRelatedNotes = async (
    ids: UUID[],
    limit: number = 10,
    similarityThreshold: number = 0.7,
  ): Promise<RelatedResult[]> => {
    if (!this.userId) throw new Error('User not authenticated - findRelatedNotes');

    const cleanIds = ids.filter((id) => isUUID(id));
    if (cleanIds.length === 0) {
      return [];
    }
    const { data, error } = await this.db.rpc('find_related_notes', {
      note_ids: cleanIds,
      limit_count: limit,
      similarity_threshold: similarityThreshold,
    });
    if (error) throw error;
    return data as RelatedResult[];
  };

  // custom fetch that uses the supabase auth token - useful for edge functions
  fetch = async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
    const url = input instanceof Request ? input.url : input.toString();
    if (!allowedUrls.some((allowedUrl) => url.startsWith(allowedUrl))) {
      throw new Error(
        `SupabaseDB fetch - Invalid URL: Only requests starting with ${allowedUrls.join(', ')} are allowed`,
      );
    }
    const {
      data: { session },
      error,
    } = await this.db.auth.getSession();
    if (error) throw new Error('SupabaseDB fetch - Error getting session', error);
    if (!session?.access_token) {
      throw new Error('SupabaseDB fetch - No valid session token found');
    }
    const headers = new Headers(init?.headers);
    headers.set('Authorization', `Bearer ${session.access_token}`);
    return fetch(url, {
      ...init,
      headers,
    });
  };

  // Get annote index for skip-over optimization - using RPC for efficiency
  // Fix database implementation to use direct queries instead of RPC
  getAnnoteIndex = async (): Promise<Record<UUID, AnnoteIndexEntry>> => {
    const index: Record<UUID, AnnoteIndexEntry> = {};
    const pageSize = 1000; // Match Supabase's default max_rows
    let offset = 0;
    let hasMore = true;

    // Fetch all annotes using pagination to bypass max_rows limit
    while (hasMore) {
      // eslint-disable-next-line no-await-in-loop
      const { data, error } = await this.db
        .from('Notes')
        .select('id, links, backlinks')
        .eq('type', 'annote')
        .is('deletedAt', null)
        .range(offset, offset + pageSize - 1);

      if (error) throw error;

      if (data && Array.isArray(data)) {
        data.forEach((row: { id: UUID; links?: UUID[]; backlinks?: UUID[] }) => {
          // Deduplicate links and backlinks when building index (matches DexieDatabase behavior)
          index[row.id] = {
            links: Array.from(new Set(row.links || [])),
            backlinks: Array.from(new Set(row.backlinks || [])),
          };
        });

        // If we got fewer results than pageSize, we've reached the end
        hasMore = data.length === pageSize;
        offset += pageSize;
      } else {
        hasMore = false;
      }
    }

    console.log(`SupabaseDB getAnnoteIndex loaded ${Object.keys(index).length} annotes`);
    return index;
  };
}

export const isSupabaseDatabase = (db: IDatabase): db is SupabaseDatabase => {
  return db && 'db' in db && db.db instanceof SupabaseClient;
};
